import { useTraduccion } from '@tallerya/i18n';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Ayuda } from './Ayuda';
import './ui.css';

/**
 * Un panel que sale por la derecha, encima de la pantalla y sin moverla: el
 * historial de una ficha (#193). En el celular ocupa la pantalla entera.
 *
 *   ┌──────────────── pantalla ─────────┬──────────────────┐
 *   │                                   │ Título ⓘ       ✕ │
 *   │  (la ficha, quieta y apagada)     │                  │
 *   │                                   │ Contenido, con   │
 *   │                                   │ su propio scroll │
 *   └───────────────────────────────────┴──────────────────┘
 *
 * Es un `<dialog>` con `showModal()`, como la `Ventana` y por lo mismo: el foco no
 * se escapa a la página de atrás y va por encima de todo sin pelear con ningún
 * `z-index`.
 *
 * **Lo que no copia de la `Ventana`: se cierra solo.** Con Escape, con la ✕ o
 * tocando fuera. La ventana no lo hace porque un formulario a medias se perdería
 * con un toque sin querer (#124); el panel es para leer, y no hay nada que perder.
 *
 * Es controlado: `abierto` decide. El contenido se dibuja solo con el panel
 * abierto, así que lo que pide datos adentro no los pide antes.
 */
export function PanelLateral({
  abierto,
  onCerrar,
  titulo,
  info,
  children,
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  /** La explicación del panel, en el globo del ⓘ junto al título. */
  info?: string;
  children: ReactNode;
}) {
  const { t } = useTraduccion();
  const dialogo = useRef<HTMLDialogElement>(null);
  const cerrar = useRef<HTMLButtonElement>(null);
  const enElFondo = useRef(false);
  const idTitulo = useId();
  const idInfo = useId();

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (abierto && !d.open) {
      d.showModal();
      // Sin esto, el foco cae en el ⓘ del título, que es lo primero que se puede tocar.
      cerrar.current?.focus();
    }
    if (!abierto && d.open) d.close();
  }, [abierto]);

  return (
    <dialog
      ref={dialogo}
      className="ui-panel"
      aria-labelledby={idTitulo}
      aria-describedby={info ? idInfo : undefined}
      // Escape: que lo cierre React y no el navegador, para que `abierto` no quede mintiendo.
      onCancel={(e) => {
        e.preventDefault();
        onCerrar();
      }}
      // Si el navegador lo cerrara por su cuenta, que `abierto` no quede diciendo que
      // sigue abierto: el botón no podría volver a abrirlo.
      onClose={() => {
        if (abierto) onCerrar();
      }}
      // Un toque en el fondo llega al `<dialog>` mismo; uno en el panel, a lo de adentro.
      // Pero un arrastre entre los dos —seleccionar texto y soltar afuera, o al revés—
      // también dispara el `click` en el `<dialog>`: cierra solo si empezó y terminó
      // en el fondo.
      onPointerDown={(e) => {
        enElFondo.current = e.target === e.currentTarget;
      }}
      onPointerUp={(e) => {
        if (e.target !== e.currentTarget) enElFondo.current = false;
      }}
      onClick={(e) => {
        if (enElFondo.current && e.target === e.currentTarget) onCerrar();
        enElFondo.current = false;
      }}
    >
      {abierto && (
        <div className="ui-panel__cuerpo">
          <div className="ui-panel__cabecera">
            <h2 id={idTitulo} className="ui-panel__titulo">
              {titulo}
            </h2>
            {info && <Ayuda tema={titulo} texto={info} tono="titulo" />}
            <button ref={cerrar} type="button" className="ui-panel__cerrar" aria-label={t('ui.panel.cerrar')} onClick={onCerrar}>
              ✕
            </button>
          </div>
          {info && (
            <span id={idInfo} hidden>
              {info}
            </span>
          )}
          <div className="ui-panel__contenido">{children}</div>
        </div>
      )}
    </dialog>
  );
}
